import { FC, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'

import './style.scss'
import 'react-datepicker/dist/react-datepicker.css'
import CONFIG from '../../../../config'
import { utils } from '../../../utils'
import { Button, Text, FormLabel, FormLabelError, FormTitle, Select, Textarea, DatePicker, InputFile, Checkbox, Autocomplete, Input, FormMessageSuccess, FormMessageError, FormFieldLoading } from '../../UI'
import { Modal, useModal } from '../../UI/Modal'
import { FamilyGroupFilter } from '..'
import { useAuth } from '../../../hooks'
import { requestService } from '../../../services'

type BudgetRequestFormProps = {
  className?: string,
  interventions: any[] | null,
  forecasts: any[] | null,
  treatingsPhysicians: any[] | null,
  familyFilter: any,
  familyFilterSelected: string,
  onFamilyFilterChange?(patientId: string): void
}

interface IBudgetRequestForm {
  tipoIntervencionId: string,
  previsionId: string,
  medicoTratante: string,
  ordenMedica?: FileList,
  fechaEstimadaIngreso: any,
  comentarios: string,
  autorizacionContacto: boolean,
  horasPabellon?: string
}

const defaultValues: IBudgetRequestForm = {
  tipoIntervencionId: '',
  previsionId: '',
  medicoTratante: '',
  ordenMedica: undefined,
  fechaEstimadaIngreso: '',
  comentarios: '',
  autorizacionContacto: true,
  horasPabellon: ''
}

const BudgetRequestForm:FC<BudgetRequestFormProps> = ({ className = '', interventions, forecasts, treatingsPhysicians, familyFilter, familyFilterSelected, onFamilyFilterChange }) => {
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, control, setValue, watch, reset } = useForm<IBudgetRequestForm>({ defaultValues })
  const [submited, setSubmited] = useState<boolean>()
  const userCurrent = useAuth()
  const modal = useModal()
  const tipoIntervencionId = watch('tipoIntervencionId', '')

  useEffect(() => {
    setForecastId()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyFilterSelected, familyFilter])

  const getUserId = () => {
    const userInfo = userCurrent.getInfo()
    return (familyFilterSelected ? familyFilterSelected : userInfo.id)
  }

  const setForecastId = () => {
    const userId: number = Number(getUserId())
    const patient: any = (familyFilter && familyFilter.data ? familyFilter.data.find((it: any) => it.id === userId) : null)
    setValue('previsionId', (patient && patient.forecast_id ? patient.forecast_id : ''))
  }

  const getTreatingsPhysiciansAutocomplete = (data: any[]) => {
    const treatingsPhysiciansData: any[] = []

    if(data && data.length) {
      data.forEach((it: any) => {
        treatingsPhysiciansData.push(`${it.nombre} ${it.apellido1} ${it.apellido2}`)
      })
    }

    return treatingsPhysiciansData
  }

  const postForm = async (fData: any, id: string) => {
    const resp: any = await requestService.postBudgetRequest(fData, id)

    if(resp && resp.status === 'Ok') {
      modal.setStatus('SUCCESS')
      reset(defaultValues)
      if(onFamilyFilterChange) onFamilyFilterChange(getUserId())
    } else {
      modal.setStatus('ERROR')
    }
    setSubmited(false)
    modal.open()
  }

  const onFileChange = (name: any, files: any) => {
    setValue(name, files)
  }

  const onFileError = (type: 'ERROR' | 'CLEAR', name: any = '', value: any = '') => {
    if(type === 'ERROR') setError(name, value)
    else if(type === 'CLEAR') clearErrors(name)
  }

  const onAutocompleteChange = (name: any, value: string) => {
    setValue(name, value)
  }

  const onFilterChange = (id: string) => {
    if(onFamilyFilterChange) onFamilyFilterChange(id)
  }

  const onSubmit = async (fData: IBudgetRequestForm) => {
    const {
      tipoIntervencionId,
      previsionId,
      medicoTratante,
      ordenMedica,
      fechaEstimadaIngreso,
      horasPabellon,
      comentarios,
      autorizacionContacto
    } = fData
    const formReg = new FormData()
    formReg.append('id_intervencion', tipoIntervencionId)
    formReg.append('c_prevision', previsionId)
    formReg.append('medico_tratante', medicoTratante)
    if(ordenMedica !== undefined && ordenMedica[0]) formReg.append('file', ordenMedica[0])
    formReg.append('fecha_estimada', `${utils.getDateFormat(fechaEstimadaIngreso, 'YYYY-MM-DD')}`)
    formReg.append('h_pabellon', horasPabellon ? horasPabellon : '')
    formReg.append('comentarios', comentarios)
    formReg.append('autoriza_contacto', autorizacionContacto ? 'Si' : 'No')
    setSubmited(true)
    postForm(formReg, getUserId())
  }

  return (
    <>
      <div className={className}>
        <FamilyGroupFilter
          data={familyFilter.data}
          dataSelected={familyFilterSelected}
          text="Solicitud para"
          onChange={onFilterChange}
        />

        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="mt-40">
          <div className="row">
            <div className="col-12 lg:col-6 lg:pr-50">
              <FormTitle>Datos de la intervención</FormTitle>

              <div className="lg:ml-20 mt-30">
                <fieldset className="lg:order-1">
                  <FormLabel htmlFor="tipoIntervencionId" required>Tipo de intervención</FormLabel>
                  <div className="position-relative mt-5">
                    <Select
                      {...register('tipoIntervencionId', { required: 'Selecciona Tipo de intervención' })}
                      id="tipoIntervencionId"
                      name="tipoIntervencionId"
                      defaultValue=""
                      tabIndex={1}
                      fullWidth
                      required
                      error={!!errors.tipoIntervencionId}
                    >
                      <option value="" disabled>Selecciona</option>
                      {interventions && interventions.length > 0 && interventions.map((it: any) => <option key={it.codigo} value={it.codigo}>{it.nombre}</option>)}
                    </Select>
                    {interventions === null && <FormFieldLoading overlay />}
                  </div>
                  {errors.tipoIntervencionId && <FormLabelError>{errors.tipoIntervencionId?.message}</FormLabelError>}
                  <Text size="12" className="mt-5">Si es más de un código de intervención o si el embarazo es gemelar o múltiple, por favor especificarlo en los comentarios.</Text>
                </fieldset>

                {(tipoIntervencionId === '2' || tipoIntervencionId === '3') &&
                  <fieldset className="lg:order-2 mt-30">
                    <FormLabel htmlFor="horasPabellon" required>Horas de pabellón</FormLabel>
                    <Input
                      {...register('horasPabellon', {
                        required: 'Digita Horas de pabellón',
                        pattern: {
                          value: CONFIG.FORM.PATTERN.NUMBER,
                          message: 'Digita solo números'
                        }
                      })}
                      id="horasPabellon"
                      name="horasPabellon"
                      placeholder="00"
                      tabIndex={2}
                      fullWidth
                      maxLength={2}
                      error={!!errors.horasPabellon}
                      className="mt-5"
                    />
                    {errors.horasPabellon && <FormLabelError>{errors.horasPabellon?.message}</FormLabelError>}
                  </fieldset>
                }

                {tipoIntervencionId !== '2' &&
                  <fieldset className="lg:order-2 mt-30">
                    <FormLabel htmlFor="previsionId" required>Previsión</FormLabel>
                    <div className="position-relative mt-5">
                      <Select
                        {...register('previsionId', { required: 'Selecciona Previsión' })}
                        id="previsionId"
                        name="previsionId"
                        defaultValue=""
                        tabIndex={2}
                        fullWidth
                        required
                        error={!!errors.previsionId}
                      >
                        <option value="" disabled>Selecciona</option>
                        {forecasts && forecasts.length > 0 && forecasts.map((it: any) => <option key={it.id} value={it.id}>{it.nombre}</option>)}
                      </Select>
                      {forecasts === null && <FormFieldLoading overlay />}
                    </div>
                    {errors.previsionId && <FormLabelError>{errors.previsionId?.message}</FormLabelError>}
                  </fieldset>
                }

                <fieldset className="lg:order-3 mt-30">
                  <FormLabel htmlFor="medicoTratante">Médico tratante</FormLabel>
                  <div className="position-relative mt-5">
                    <Autocomplete
                      {...register('medicoTratante')}
                      id="medicoTratante"
                      name="medicoTratante"
                      suggestions={getTreatingsPhysiciansAutocomplete(treatingsPhysicians ? treatingsPhysicians : [])}
                      placeholder="Nombre completo"
                      fullWidth
                      tabIndex={3}
                      onChange={onAutocompleteChange}
                    />
                    {treatingsPhysicians === null && <FormFieldLoading overlay />}
                  </div>
                </fieldset>

                <fieldset className="lg:order-3 mt-30">
                  <FormLabel htmlFor="ordenMedica" required={tipoIntervencionId === '4'}>Orden médica</FormLabel>

                  <div className="input-file-button mt-5">
                    <InputFile
                      {...register('ordenMedica', { required: (tipoIntervencionId === '4' ? 'Selecciona Orden médica' : false) })}
                      id="ordenMedica"
                      name="ordenMedica"
                      butonText="CARGAR ARCHIVO"
                      tabIndex={4}
                      className="budget-request-btn-file"
                      // multiple
                      onChange={onFileChange}
                      onError={onFileError}
                    />
                  </div>
                  {errors.ordenMedica && <FormLabelError>{errors.ordenMedica?.message}</FormLabelError>}
                </fieldset>
              </div>
            </div>

            <div className="col-12 lg:col-6 lg:pl-50 mt-60 lg:mt-0">
              <FormTitle>Datos de admisión</FormTitle>

              <div className="lg:ml-20 mt-30">
                <fieldset className="lg:order-5">
                  <FormLabel htmlFor="fechaEstimadaIngreso" required>Fecha estimada de ingreso</FormLabel>
                  <div className="mt-5">
                    <Controller
                      control={control}
                      name='fechaEstimadaIngreso'
                      render={({ field }) => (
                        <DatePicker
                          {...register('fechaEstimadaIngreso', { required: 'Selecciona Fecha estimada de ingreso' })}
                          selected={field.value}
                          placeholderText="DD/MM/YYYY"
                          tabIndex={5}
                          openToDate={new Date('2000/01/01')}
                          showYearDropdown
                          showMonthDropdown
                          fullWidth
                          error={!!errors.fechaEstimadaIngreso}
                          onChange={(date) => field.onChange(date)}
                        />
                      )}
                    />
                  </div>
                  {errors.fechaEstimadaIngreso && <FormLabelError>{errors.fechaEstimadaIngreso?.message}</FormLabelError>}
                </fieldset>

                <fieldset className="lg:order-7 mt-30">
                  <FormLabel htmlFor="comentarios">Comentarios</FormLabel>
                  <Textarea
                    {...register('comentarios')}
                    id="comentarios"
                    name="comentarios"
                    tabIndex={6}
                    fullWidth
                    style={{ minHeight: '148px' }}
                    className="mt-5"
                  />
                </fieldset>

                <fieldset className="lg:order-5 mt-30">
                  <FormLabel>Autorización de contacto</FormLabel>

                  <div className="mt-5">
                    <Checkbox
                      {...register('autorizacionContacto')}
                      id="autorizacionContacto"
                      name="autorizacionContacto"
                      tabIndex={7}
                    />
                    <FormLabel
                      htmlFor="autorizacionContacto"
                      size="12"
                      weight="default"
                      className="d-flex"
                    >
                      Autorizo a Clínica INDISA para que me contacte por productos y servicios que ofrezca.
                    </FormLabel>
                  </div>
                </fieldset>
              </div>
            </div>

            <div className="col-12 mt-40">
              <FormTitle required>Campo obligatorio</FormTitle>
            </div>

            <div className="col-12 mt-40 budget-request-btn-wrapper">
              <Button
                type="submit"
                size="md"
                tabIndex={8}
                fullWidth
                disabled={submited}
              >ENVIAR</Button>
            </div>
          </div>
        </form>
      </div>

      <Modal show={modal.show} maxWidth="415px" onClose={() => modal.close()}>
        {modal.status === 'SUCCESS' &&
          <FormMessageSuccess
            className="py-60 px-50"
            title="¡La solicitud ha sido recibida con éxito!"
            message="En un plazo de 48 horas te responderemos por correo electrónico"
          />
        }
        {modal.status === 'ERROR' &&
          <FormMessageError
            className="py-60 px-50"
            message="No ha sido posible guardar solicitud de presupuestos"
            onClick={() => modal.close()}
          />
        }
      </Modal>
    </>
  )
}

export { BudgetRequestForm }